import { API_CONFIG, getStandardHeaders } from "@/config/envConfig";
import { isTokenExpired } from "@/utils/jwtUtils";

export interface ApiResponse<T = any> {
    data?: T;
    error?: string;
    status: number;
    success: boolean;
}

export interface ApiRequestOptions {
    method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
    headers?: Record<string, string>;
    body?: any;
    token?: string;
    timeout?: number;
    retries?: number; // retry support
}

class ApiClient {
    private baseUrl: string;
    private defaultTimeout: number = 30000; // 30s
    private defaultRetries: number = 0; // no retries by default
    private sessionExpiredHandler: (() => void) | null = null;

    constructor() {
        this.baseUrl = API_CONFIG.API_BASE;
    }

    /**
     * Set session expired handler for 401 errors
     */
    setSessionExpiredHandler(handler: () => void) {
        this.sessionExpiredHandler = handler;
    }

    /**
     * Handle 401 responses for specific endpoints
     */
    private handle401Response(endpoint: string, token?: string) {
        // Only handle 401 for profile and hasura token endpoints
        const isProfileEndpoint = endpoint.includes('/profile') || endpoint.includes('/hasura');
        
        if (isProfileEndpoint && this.sessionExpiredHandler) {
            console.log('🔒 401 detected on profile/hasura endpoint - triggering session expired handler');
            this.sessionExpiredHandler();
        }
    }

    /**
     * Generic API request method
     */
    async request<T = any>(
        endpoint: string,
        options: ApiRequestOptions = {}
    ): Promise<ApiResponse<T>> {
        const {
            method = "GET",
            headers = {},
            body,
            token,
            timeout = this.defaultTimeout,
            retries = this.defaultRetries,
        } = options;

        const url = endpoint.startsWith("http")
            ? endpoint
            : `${this.baseUrl}${endpoint}`;

        const requestHeaders = {
            ...getStandardHeaders(!!token, token),
            ...headers,
        };

        const config: RequestInit = {
            method,
            headers: requestHeaders,
            signal: AbortSignal.timeout(timeout),
        };

        if (body && method !== "GET") {
            config.body = typeof body === "string" ? body : JSON.stringify(body);
        }

        try {
            // Check token expiry before making request (only for profile/hasura endpoints)
            const isProfileEndpoint = endpoint.includes('/profile') || endpoint.includes('/hasura');
            if (token && isProfileEndpoint) {
                const expired = isTokenExpired(token);
                if (expired === true) {
                    console.log('🔒 Token expired before request - triggering session expired handler');
                    this.handle401Response(endpoint, token);
                    return {
                        status: 401,
                        success: false,
                        error: 'Token expired'
                    };
                }
            }

            const response = await this.fetchWithRetry(url, config, retries);
            const responseData = await this.parseResponse(response);

            // Handle 401 responses
            if (response.status === 401) {
                this.handle401Response(endpoint, token);
            }

            return {
                data: responseData,
                status: response.status,
                success: response.ok,
                error: response.ok
                    ? undefined
                    : this.formatErrorMessage(responseData) ||
                    `HTTP ${response.status}: ${response.statusText}`,
            };
        } catch (error) {
            console.error("API Request failed:", error);
            return {
                status: 0,
                success: false,
                error:
                    error instanceof Error ? error.message : "Unknown error occurred",
            };
        }
    }

    /**
     * Retry mechanism with exponential backoff
     */
    private async fetchWithRetry(
        url: string,
        config: RequestInit,
        retries: number
    ): Promise<Response> {
        for (let i = 0; i <= retries; i++) {
            try {
                return await fetch(url, config);
            } catch (err) {
                if (i === retries) throw err;
                const delay = 500 * Math.pow(2, i); // 500ms, 1s, 2s...
                if (import.meta.env.DEV) {
                    console.warn(`Retrying [${i + 1}/${retries}] after ${delay}ms`);
                }
                await new Promise((r) => setTimeout(r, delay));
            }
        }
        throw new Error("Max retries reached");
    }

    /**
     * GET
     */
    async get<T = any>(endpoint: string, token?: string): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { method: "GET", token });
    }

    /**
     * POST
     */
    async post<T = any>(
        endpoint: string,
        body?: any,
        token?: string,
        additionalHeaders?: Record<string, string>
    ): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: "POST",
            body,
            token,
            headers: additionalHeaders,
        });
    }

    /**
     * PUT
     */
    async put<T = any>(
        endpoint: string,
        body?: any,
        token?: string,
        additionalHeaders?: Record<string, string>
    ): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: "PUT",
            body,
            token,
            headers: additionalHeaders,
        });
    }

    /**
     * DELETE
     */
    async delete<T = any>(
        endpoint: string,
        token?: string
    ): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { method: "DELETE", token });
    }

    /**
     * PATCH
     */
    async patch<T = any>(
        endpoint: string,
        body?: any,
        token?: string,
        additionalHeaders?: Record<string, string>
    ): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: "PATCH",
            body,
            token,
            headers: additionalHeaders,
        });
    }

    /**
     * Parse response based on content type
     */
    private async parseResponse(response: Response): Promise<any> {
        const contentType = response.headers.get("content-type");

        if (contentType?.includes("application/json")) {
            return response.json();
        }
        if (contentType?.includes("text/")) {
            return response.text();
        }
        return response.blob();
    }

    /**
     * Utility for building PEP endpoints
     */
    buildPepEndpoint(userId: string | number, path: string): string {
        return `/api/pep/${userId}${path}`;
    }

    getBaseUrl(): string {
        return this.baseUrl;
    }

    /**
     * Format error message from API response
     * Handles the format: { "messages": null, "errors": ["title", "body1", "body2"] }
     */
    private formatErrorMessage(responseData: any): string | undefined {
        if (responseData?.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
            const title = responseData.errors[0]?.trim();
            const body = responseData.errors.slice(1).map((err: string) => err?.trim()).filter(Boolean);
            
            if (body.length > 0) {
                return `${title}\n${body.join('\n')}`;
            }
            return title;
        }
        
        // Fallback to message field if available
        return responseData?.message;
    }
}

export const apiClient = new ApiClient();
export default apiClient;